import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Banknote } from 'lucide-react';
import { useDebts } from '../hooks/useDebts.js';
import DebtProgressBar from '../components/DebtProgressBar.jsx';
import DebtForm from '../components/DebtForm.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
import { formatCurrency, formatDate } from '../lib/format.js';
import {
  SOURCE_TYPE_LABELS,
  SOURCE_TYPE_BADGE,
  REPAYMENT_MODE_LABELS,
  STATUS_LABELS,
  computeEndDate,
} from '../lib/debtHelpers.js';

// ── KPI Card ──────────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, accentClass = '' }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 shadow-sm px-4 py-4 flex flex-col gap-1">
      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
      <p className={`text-2xl font-bold tabular-nums ${accentClass || 'text-slate-900'}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function DebtsPage() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useDebts();
  const [formOpen, setFormOpen] = useState(false);

  const debts = Array.isArray(data) ? data : data?.debts ?? [];
  const active = debts.filter((d) => d.status === 'active');

  // ── KPIs ──
  const totalCapital = debts.reduce((s, d) => s + (d.capital_cents ?? 0), 0);
  const totalRemaining = active.reduce((s, d) => s + (d.remaining_cents ?? 0), 0);
  const totalMonthly = active.reduce((s, d) => s + (d.monthly_payment_cents ?? 0), 0);
  const globalPct = totalCapital > 0
    ? Math.round(((totalCapital - totalRemaining) / totalCapital) * 100)
    : 0;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Dettes</h1>
          <p className="text-sm text-slate-500">Suivi des instruments de dette externe</p>
        </div>
        <button
          type="button"
          onClick={() => setFormOpen(true)}
          className="flex items-center gap-1.5 bg-slate-800 text-white text-sm font-medium px-3 py-2 rounded-lg hover:bg-slate-700 transition-colors shrink-0"
          aria-label="Créer une nouvelle dette"
        >
          <Plus size={15} />
          <span className="hidden sm:inline">Nouvelle dette</span>
          <span className="sm:hidden">Nouveau</span>
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          Impossible de charger les dettes : {error.message}
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {!isLoading && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              label="Total emprunté"
              value={formatCurrency(totalCapital)}
              sub={`${debts.length} instrument${debts.length > 1 ? 's' : ''}`}
            />
            <KpiCard
              label="Capital restant dû"
              value={formatCurrency(totalRemaining)}
              sub="Dettes actives"
              accentClass={totalRemaining > 0 ? 'text-amber-700' : 'text-emerald-700'}
            />
            <KpiCard
              label="Provision mensuelle"
              value={formatCurrency(totalMonthly)}
              sub="Total mensualités actives"
            />
            <KpiCard
              label="% remboursé global"
              value={`${globalPct} %`}
              sub="Capital amorti / emprunté"
              accentClass={globalPct >= 50 ? 'text-emerald-700' : 'text-slate-900'}
            />
          </div>

          {/* Empty state */}
          {debts.length === 0 && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 px-6 py-16 text-center">
              <Banknote size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm font-medium text-slate-700 mb-1">Aucune dette enregistrée</p>
              <p className="text-sm text-slate-500 mb-4">
                Créez votre premier instrument de dette pour en suivre la progression.
              </p>
              <button
                type="button"
                onClick={() => setFormOpen(true)}
                className="inline-flex items-center gap-1.5 bg-slate-800 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-slate-700 transition-colors"
              >
                <Plus size={14} /> Nouvelle dette
              </button>
            </div>
          )}

          {/* Table */}
          {debts.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left text-xs font-medium text-slate-500 px-4 py-3 sticky left-0 bg-slate-50 whitespace-nowrap">Nom</th>
                      <th className="text-left text-xs font-medium text-slate-500 px-4 py-3 whitespace-nowrap">Source</th>
                      <th className="text-right text-xs font-medium text-slate-500 px-4 py-3 whitespace-nowrap">Capital</th>
                      <th className="text-left text-xs font-medium text-slate-500 px-4 py-3 whitespace-nowrap hidden md:table-cell">Mode</th>
                      <th className="text-right text-xs font-medium text-slate-500 px-4 py-3 whitespace-nowrap hidden md:table-cell">Mensualité</th>
                      <th className="text-left text-xs font-medium text-slate-500 px-4 py-3 whitespace-nowrap min-w-[140px]">Progression</th>
                      <th className="text-center text-xs font-medium text-slate-500 px-4 py-3 whitespace-nowrap">Statut</th>
                      <th className="text-left text-xs font-medium text-slate-500 px-4 py-3 whitespace-nowrap hidden lg:table-cell">Fin projetée</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {debts.map((d) => {
                      const sourceBadge = SOURCE_TYPE_BADGE[d.source_type] ?? SOURCE_TYPE_BADGE.other;
                      const sourceLabel = SOURCE_TYPE_LABELS[d.source_type] ?? d.source_type;
                      const statusCfg = STATUS_LABELS[d.status] ?? STATUS_LABELS.active;
                      const modeLabel = REPAYMENT_MODE_LABELS[d.repayment_mode] ?? d.repayment_mode;
                      const endDate = computeEndDate(d);

                      return (
                        <tr
                          key={d.id}
                          onClick={() => navigate(`/dettes/${d.id}`)}
                          className="hover:bg-slate-50 cursor-pointer transition-colors"
                        >
                          <td className="px-4 py-3 sticky left-0 bg-white hover:bg-slate-50 font-medium text-slate-900 whitespace-nowrap">
                            {d.name}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sourceBadge}`}>
                              {sourceLabel}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700 whitespace-nowrap">
                            {formatCurrency(d.capital_cents)}
                          </td>
                          <td className="px-4 py-3 text-slate-600 whitespace-nowrap hidden md:table-cell">
                            {modeLabel}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-700 whitespace-nowrap hidden md:table-cell">
                            {formatCurrency(d.monthly_payment_cents)}
                          </td>
                          <td className="px-4 py-3 min-w-[140px]">
                            <DebtProgressBar
                              paid_cents={d.paid_cents ?? 0}
                              total_cents={d.capital_cents ?? 0}
                              size="sm"
                              isOverdue={d.is_overdue}
                            />
                            <span className="text-xs text-slate-500 tabular-nums">
                              {d.pct_paid ?? 0}&nbsp;%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusCfg.badge}`}>
                              {statusCfg.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-500 whitespace-nowrap hidden lg:table-cell">
                            {formatDate(endDate)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      <DebtForm open={formOpen} onClose={() => setFormOpen(false)} />
    </div>
  );
}
